import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Avatar,
    Button,
    Paper,
    CircularProgress,
    Alert,
    IconButton,
    Chip,
    Pagination,
    Skeleton
} from '@mui/material';
import { ArrowBack, Person, PersonAdd, PersonRemove } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { socialAPI, toggleFollow } from '../utils/api';

const FollowersList = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [followers, setFollowers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [followingActions, setFollowingActions] = useState({});
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [userInfo, setUserInfo] = useState(null);

    const fetchFollowers = async (pageNum = 1) => {
        try {
            setLoading(true);
            const response = await socialAPI.getFollowers(userId, pageNum);
            setFollowers(response.data.followers || []);
            setTotalPages(response.data.pagination?.pages || 1);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchUserInfo = async () => {
        try {
            const response = await socialAPI.getUserProfile(userId);
            setUserInfo(response.data?.user || null);
        } catch (err) {
            console.error('Error fetching user info:', err);
        }
    };

    useEffect(() => {
        if (userId) {
            fetchFollowers(page);
            fetchUserInfo();
        }
    }, [userId, page]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleFollow = async (targetUserId) => {
        if (followingActions[targetUserId]) return;

        try {
            setFollowingActions(prev => ({ ...prev, [targetUserId]: true }));
            
            const result = await toggleFollow(targetUserId);
            
            setFollowers(prev => prev.map(follower => 
                follower.id === targetUserId 
                    ? { ...follower, is_following: result.following }
                    : follower
            ));
        } catch (err) {
            console.error('Error toggling follow:', err);
        } finally {
            setFollowingActions(prev => ({ ...prev, [targetUserId]: false }));
        }
    };

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    const getFollowButtonProps = (follower) => {
        if (follower.is_current_user) {
            return null;
        }

        if (follower.is_following) {
            return {
                variant: 'outlined',
                startIcon: <PersonRemove />,
                children: 'Unfollow'
            };
        }

        return {
            variant: 'contained',
            startIcon: <PersonAdd />,
            children: follower.is_mutual ? 'Follow Back' : 'Follow'
        };
    };

    if (loading && page === 1) {
        return (
            <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                    {[1, 2, 3, 4].map((idx) => (
                        <Box key={idx} display="flex" alignItems="center" gap={1.5} sx={{ py: 1.2 }}>
                            <Skeleton variant="circular" width={40} height={40} />
                            <Box flex={1}>
                                <Skeleton variant="text" width="42%" />
                                <Skeleton variant="text" width="28%" />
                            </Box>
                            <Skeleton variant="rounded" width={96} height={34} />
                        </Box>
                    ))}
                </Paper>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
                <Alert severity="error">
                    {error}
                </Alert>
            </Container>
        );
    }

    const isCurrentUser = String(userId) === String(user?.id);

    return (
        <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
            <Paper
                variant="outlined"
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    mb: 3,
                    borderRadius: 3,
                    background:
                        'radial-gradient(220px 140px at 90% -30%, rgba(255, 110, 77, 0.18), transparent 70%), linear-gradient(135deg, rgba(14, 123, 255, 0.12), rgba(0, 165, 255, 0.07))',
                }}
            >
                <Box display="flex" alignItems="center" gap={1}>
                    <IconButton aria-label="Back" onClick={() => navigate(-1)}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 800 }}>
                        {userInfo ? `${userInfo.username}'s Followers` : 'Followers'}
                    </Typography>
                </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }}>
                {followers.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Person sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">
                            No followers yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {isCurrentUser ? 
                                "You don't have any followers yet." : 
                                "This user doesn't have any followers yet."
                            }
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <List>
                            {followers.map((follower) => {
                                const buttonProps = getFollowButtonProps(follower);
                                
                                return (
                                    <ListItem
                                        key={follower.id}
                                        divider
                                        sx={{
                                            borderRadius: 2,
                                            transition: 'background-color 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: 'rgba(14, 123, 255, 0.06)',
                                            },
                                        }}
                                    >
                                        <ListItemAvatar>
                                            <Avatar 
                                                src={follower.avatar_url} 
                                                alt={follower.username}
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => navigate(`/profile/${follower.id}`)}
                                            />
                                        </ListItemAvatar>
                                        <ListItemText 
                                            primary={
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Typography 
                                                        variant="subtitle1" 
                                                        component="span"
                                                        sx={{ cursor: 'pointer' }}
                                                        onClick={() => navigate(`/profile/${follower.id}`)}
                                                    >
                                                        {follower.username}
                                                    </Typography>
                                                    {follower.is_mutual && (
                                                        <Chip 
                                                            label="Mutual" 
                                                            size="small" 
                                                            color="primary"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Box>
                                            }
                                            secondary={follower.name}
                                        />
                                        {buttonProps && (
                                            <Button
                                                {...buttonProps}
                                                size="small"
                                                disabled={followingActions[follower.id]}
                                                onClick={() => handleFollow(follower.id)}
                                            >
                                                {followingActions[follower.id] ? (
                                                    <CircularProgress size={16} />
                                                ) : (
                                                    buttonProps.children
                                                )}
                                            </Button>
                                        )}
                                    </ListItem>
                                );
                            })}
                        </List>

                        {totalPages > 1 && (
                            <Box display="flex" justifyContent="center" mt={3}>
                                <Pagination 
                                    count={totalPages} 
                                    page={page} 
                                    onChange={handlePageChange}
                                    color="primary"
                                />
                            </Box>
                        )}
                    </>
                )}
            </Paper>
        </Container>
    );
};

export default FollowersList;
